import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { useIntersectionObserver, useIsMobileDevice, useLockScroll, useWindowSize, } from '@hivebrite/react-hooks';
import { useTheme, zIndices } from '@design-system/theme';
import { Portal } from './components/Portal';
import { TooltipPopover } from './components/TooltipPopover';
import { OPPOSITE_POSITIONS, POSITIONS, TYPES } from './constants';
export const Tooltip = (_a) => {
    var { children, childrenElement, enterDelay = 0, fixedParent = false, hideOnScreenReader = false, id, initialPosition = POSITIONS.TOP, inline, leaveDelay = 0, maxWidth, onClose = () => { }, onOpen = () => { }, portalTarget, removeHoverEffect = false, shouldHideArrow = false, shouldRenderTooltip = false, textAlign = 'center', type = TYPES.INFO } = _a, otherProps = __rest(_a, ["children", "childrenElement", "enterDelay", "fixedParent", "hideOnScreenReader", "id", "initialPosition", "inline", "leaveDelay", "maxWidth", "onClose", "onOpen", "portalTarget", "removeHoverEffect", "shouldHideArrow", "shouldRenderTooltip", "textAlign", "type"]);
    const [coords, setCoords] = useState(null);
    const parentRef = useRef(null);
    const tooltipRef = useRef(null);
    const [shouldRender, setShouldRender] = useState(false);
    const size = useWindowSize();
    const isMobileDevice = useIsMobileDevice();
    const [tooltipPosition, setTooltipPosition] = useState(initialPosition);
    const isRepositioned = useRef(false);
    const theme = useTheme();
    useEffect(() => {
        const timeoutID = setTimeout(() => {
            if ((shouldRender || shouldRenderTooltip) && (parentRef === null || parentRef === void 0 ? void 0 : parentRef.current)) {
                const rect = parentRef.current.getBoundingClientRect();
                const scrollY = fixedParent ? 0 : window.scrollY;
                const scrollX = fixedParent ? 0 : window.scrollX;
                const left = rect.x + rect.width / 2 + scrollX;
                const top = rect.y - rect.height / 2 + scrollY;
                if (!coords ||
                    coords.top !== top ||
                    coords.left !== left ||
                    coords.parentWidth !== rect.width) {
                    setCoords({
                        left,
                        parentHeight: rect.height,
                        parentWidth: rect.width,
                        top,
                    });
                }
            }
        }, 200);
        return () => {
            clearTimeout(timeoutID);
        };
    }, [coords, fixedParent, shouldRender, shouldRenderTooltip, size]);
    const prevVisibleRef = useRef(false);
    const isVisible = shouldRenderTooltip || shouldRender;
    useEffect(() => {
        if (isVisible && !prevVisibleRef.current) {
            onOpen();
        }
        else if (!isVisible && prevVisibleRef.current) {
            onClose();
        }
        prevVisibleRef.current = isVisible;
    }, [isVisible, onClose, onOpen]);
    const timeoutRef = useRef(undefined);
    const handleMouseEnter = useCallback(() => {
        if (removeHoverEffect || isMobileDevice) {
            return;
        }
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(setShouldRender, enterDelay, true);
    }, [enterDelay, isMobileDevice, removeHoverEffect]);
    const handleMouseLeave = useCallback(() => {
        if (removeHoverEffect || isMobileDevice) {
            return;
        }
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(setShouldRender, leaveDelay, false);
    }, [isMobileDevice, leaveDelay, removeHoverEffect]);
    const handleIntersectionObserver = useCallback(([entry]) => {
        if (entry.rootBounds &&
            entry.intersectionRatio < 1 &&
            !isRepositioned.current) {
            setTooltipPosition((prevState) => OPPOSITE_POSITIONS[prevState]);
            isRepositioned.current = true;
        }
    }, []);
    const hasTooltip = coords && childrenElement && isVisible;
    useLockScroll({
        enabled: (removeHoverEffect && shouldRenderTooltip) || false,
    });
    useIntersectionObserver(tooltipRef, handleIntersectionObserver);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    return (_jsxs(StyledContainer, { "$inline": inline, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [hasTooltip && (_jsx(Portal, { target: portalTarget, children: _jsx(TooltipPopover, { coords: coords, fixedParent: fixedParent, hideOnScreenReader: hideOnScreenReader, id: id, maxWidth: maxWidth, ref: tooltipRef, removeHoverEffect: removeHoverEffect, shouldHideArrow: shouldHideArrow, shouldRender: shouldRender, shouldRenderTooltip: shouldRenderTooltip, textAlign: textAlign, theme: theme, tooltipPosition: tooltipPosition, type: type, children: typeof childrenElement === 'function'
                        ? childrenElement({ onClose })
                        : childrenElement }) })), isVisible && removeHoverEffect ? (_jsx(StyledBox, { "data-testid": "tooltip-without-hover", ref: parentRef, children: children })) : (_jsx("div", Object.assign({ "data-testid": "tooltip-with-hover", ref: parentRef }, otherProps, { children: children })))] }));
};
const StyledContainer = styled.div `
  display: ${({ $inline }) => ($inline ? 'inline-block' : 'block')};
`;
const StyledBox = styled.div `
  box-shadow: 0 0 0 99999px rgba(0, 0, 0, 0.5);
  position: relative;
  z-index: ${zIndices.tooltip} !important;
  border-radius: 3px;
`;
//# sourceMappingURL=Tooltip.js.map